// Approved legal copy (effective October 29, 2026). Edit text here only.
// Each section: { h, body: [string | { list: string[] }] }

export const LEGAL_EFFECTIVE_DATE = "October 29, 2026";

export const PRIVACY_POLICY = {
  title: "Squad Fee Privacy Policy",
  intro: [
    "Squad Fee is operated by Rayven Tech LLC, a Florida limited liability company (“Rayven Tech,” “Squad Fee,” “we,” “us,” or “our”).",
    "This Privacy Policy explains how we collect, use, store, and share information when you use Squad Fee, including our website, web application, and related services (collectively, the “Service”).",
  ],
  sections: [
    {
      h: "Information we collect",
      body: [
        "We may collect information that you provide directly to Squad Fee, including:",
        {
          list: [
            "Name and email address",
            "Account and organization information",
            "Team, roster, and player information",
            "Player names, jersey numbers, and related team information",
            "Parent or guardian contact information",
            "Fees, amounts owed, due dates, and payment-status information",
            "Information about payments users report as having been made outside Squad Fee",
            "Payment methods reported by users, such as Zelle, Venmo, Cash App, cash, or other external methods",
            "Payment receipts or other files voluntarily uploaded as proof of payment",
            "Communications with Squad Fee support",
            "Other information voluntarily entered into the Service",
          ],
        },
        "Squad Fee is not designed for users to submit Social Security numbers, medical records, banking passwords, payment-account credentials, or other unnecessary highly sensitive information. Users should avoid including such information in uploaded receipts or other content.",
      ],
    },
    {
      h: "Information about youth players",
      body: [
        "Squad Fee may be used by adult coaches, team managers, parents, guardians, and other authorized adults to manage information relating to youth teams and players.",
        "Squad Fee accounts are intended for individuals who are at least 18 years old. Children should not create their own Squad Fee accounts.",
        "Adults who enter information about a player are responsible for having appropriate authority to provide and use that information for team-management purposes.",
      ],
    },
    {
      h: "How we use information",
      body: [
        "We may use information to:",
        {
          list: [
            "Create and maintain user accounts",
            "Operate teams and rosters",
            "Track fees, amounts owed, payment status, and payment history",
            "Facilitate communication between authorized team participants",
            "Display reported payment information and receipts to appropriate users",
            "Send account verification, password reset, fee, reminder, and other service-related emails",
            "Provide customer support",
            "Maintain, secure, troubleshoot, and improve the Service",
            "Detect or prevent fraud, misuse, or security incidents",
            "Comply with legal obligations",
          ],
        },
      ],
    },
    {
      h: "External payments",
      body: [
        "Squad Fee currently does not process, hold, transfer, or receive team or player fee payments.",
        "Payments may occur through third-party services or offline methods such as Zelle, Venmo, Cash App, cash, or other methods selected by users.",
        "Squad Fee may record information users provide about those payments, but Squad Fee does not receive users’ banking credentials through those external payment transactions.",
      ],
    },
    {
      h: "Payment receipts",
      body: [
        "Users may choose to upload receipts or other proof of payment.",
        "These materials are intended to be available only within the appropriate Squad Fee team/account workflow and are not intended to be publicly displayed.",
        "Users should remove or obscure unnecessary sensitive information before uploading a receipt.",
      ],
    },
    {
      h: "Service providers",
      body: [
        "We may use third-party service providers to operate Squad Fee, including providers for hosting, infrastructure, email delivery, data storage, security, analytics, and other technical services.",
        "These providers may process information on our behalf as necessary to provide their services.",
        "If Squad Fee introduces subscription billing in the future, payment information for Squad Fee subscriptions may be processed by a third-party payment processor. Appropriate disclosures will be provided when paid subscriptions are introduced.",
      ],
    },
    {
      h: "How we share information",
      body: [
        "We do not sell personal information.",
        "We may share information:",
        {
          list: [
            "With authorized users within the applicable organization or team when necessary to provide Squad Fee’s functionality",
            "With service providers working on our behalf",
            "When required by law, court order, or valid legal process",
            "When reasonably necessary to protect Squad Fee, Rayven Tech LLC, our users, or others from fraud, abuse, security threats, or unlawful activity",
            "In connection with a merger, acquisition, financing, sale of assets, or similar business transaction, subject to applicable law",
          ],
        },
      ],
    },
    {
      h: "Data security",
      body: [
        "We use reasonable administrative, technical, and organizational measures designed to protect information maintained by Squad Fee.",
        "No internet-based service can guarantee absolute security. Users are responsible for protecting their account credentials and should notify us if they believe their account has been compromised.",
      ],
    },
    {
      h: "Data retention",
      body: [
        "We may retain information for as long as reasonably necessary to provide the Service, maintain legitimate business records, resolve disputes, enforce agreements, meet legal obligations, and protect the security and integrity of Squad Fee.",
        "Retention periods may vary depending on the type of information and why it is maintained.",
      ],
    },
    {
      h: "User choices and requests",
      body: [
        "Users may contact us regarding questions or requests concerning their personal information.",
        "Certain information may need to be retained when required by law or for legitimate security, fraud-prevention, accounting, dispute-resolution, or recordkeeping purposes.",
        "Additional rights may apply depending on where a user resides.",
      ],
    },
    {
      h: "Cookies and technical information",
      body: [
        "Squad Fee may use cookies, local storage, logs, and similar technologies necessary to authenticate users, maintain sessions, secure the Service, understand technical performance, and provide functionality.",
        "We may also collect technical information such as browser type, device information, IP address, timestamps, and application activity for security, troubleshooting, and service improvement.",
      ],
    },
    {
      h: "Third-party services",
      body: [
        "Squad Fee may contain references or links to third-party services. Their privacy practices are governed by their own policies, and Squad Fee is not responsible for the privacy practices of independent third parties.",
      ],
    },
    {
      h: "Changes to this Privacy Policy",
      body: [
        "We may update this Privacy Policy as Squad Fee evolves.",
        "When appropriate, we may provide notice of material changes through the Service, by email, or by updating the effective date above.",
      ],
    },
  ],
  contactIntro: "Questions about this Privacy Policy or Squad Fee’s privacy practices may be directed to:",
};

export const TERMS_OF_SERVICE = {
  title: "Squad Fee Terms of Service",
  intro: [
    "These Terms of Service (“Terms”) govern your access to and use of Squad Fee, a service operated by Rayven Tech LLC, a Florida limited liability company (“Rayven Tech,” “Squad Fee,” “we,” “us,” or “our”).",
    "By creating an account or using Squad Fee, you agree to these Terms.",
  ],
  sections: [
    {
      h: "Eligibility",
      body: [
        "You must be at least 18 years old to create or operate a Squad Fee account.",
        "Squad Fee may be used by adult coaches, team managers, organization administrators, parents, guardians, and other authorized adults.",
      ],
    },
    {
      h: "The Squad Fee service",
      body: [
        "Squad Fee provides software designed to help teams and organizations manage information such as teams and rosters, players and parent/guardian information, fees and amounts owed, due dates, reported payments, payment status and history, payment receipts, reminders, and related team financial records.",
        "Squad Fee is a recordkeeping, organization, and communication platform.",
      ],
    },
    {
      h: "External team payments",
      body: [
        "Squad Fee does not currently process, hold, transfer, receive, or control team or player fee payments.",
        "Users may make payments independently using services or methods such as Zelle, Venmo, Cash App, cash, or other methods selected by the applicable parties.",
        "Information displayed in Squad Fee regarding an external payment is based on information reported or confirmed by users.",
        "Squad Fee does not independently guarantee that a reported payment was sent, received, accurate, refundable, or valid.",
        "Coaches, organizations, parents, and other users remain responsible for verifying payments and resolving payment disputes directly with one another.",
        "Squad Fee is not a bank, payment processor, money transmitter, escrow service, or party to those external transactions.",
      ],
    },
    {
      h: "Free Early Access",
      body: [
        "Squad Fee may provide complimentary Early Access while the Service is being introduced and developed.",
        "Early Access is temporary and does not constitute a promise that Squad Fee will remain free permanently.",
        "Rayven Tech LLC may introduce paid subscription plans, modify Early Access, or discontinue complimentary access in the future.",
        "Users will receive appropriate notice before paid access is required.",
        "Participation in Free Early Access will not cause a user to be automatically charged when paid subscriptions are introduced.",
        "A user must affirmatively select a paid subscription and provide or authorize an applicable payment method before Squad Fee charges that user for a subscription.",
      ],
    },
    {
      h: "Accounts and security",
      body: [
        "You are responsible for providing accurate account information, maintaining the confidentiality of your login credentials, activity conducted through your account, and promptly notifying Squad Fee if you suspect unauthorized access.",
        "You may not access another user’s account without authorization.",
      ],
    },
    {
      h: "Team and player information",
      body: [
        "Users who create organizations, teams, rosters, or player records are responsible for ensuring that they have appropriate authority to enter and use that information.",
        "Because Squad Fee may be used for youth teams, adult users are responsible for appropriately handling information relating to minors.",
        "Children should not create their own Squad Fee accounts.",
      ],
    },
    {
      h: "User content and uploaded receipts",
      body: [
        "Users may submit information and content to Squad Fee, including team information, player information, payment records, and payment receipts.",
        "You retain ownership of content you provide.",
        "You grant Rayven Tech LLC the limited rights necessary to host, store, process, display, transmit, and otherwise use that content solely as necessary to operate, secure, maintain, and improve Squad Fee.",
        "Users should not upload unnecessary sensitive information.",
        "We may remove content that violates these Terms, creates a security risk, violates law, infringes another person’s rights, or threatens the operation of the Service.",
      ],
    },
    {
      h: "Acceptable use",
      body: [
        "You may not use Squad Fee to violate applicable law; commit fraud or impersonate another person; access accounts or data without authorization; upload malicious software or harmful content; attempt to interfere with or compromise Squad Fee’s security or infrastructure; scrape, probe, reverse engineer, or abuse the Service except where such restrictions are prohibited by law; use Squad Fee to harass, threaten, exploit, or harm another person; or submit information you do not have authority to use.",
      ],
    },
    {
      h: "Ownership of Squad Fee",
      body: [
        "Squad Fee, including its software, branding, design, interfaces, features, and other proprietary materials, is owned by Rayven Tech LLC or its licensors and is protected by applicable intellectual-property laws.",
        "These Terms do not transfer ownership of Squad Fee or its intellectual property to users.",
      ],
    },
    {
      h: "Service changes and availability",
      body: [
        "Squad Fee is an evolving service.",
        "We may add, modify, suspend, or discontinue features as the Service develops.",
        "We do not guarantee uninterrupted or error-free operation. Maintenance, technical problems, third-party outages, security incidents, or other circumstances may temporarily affect availability.",
      ],
    },
    {
      h: "Suspension and termination",
      body: [
        "We may suspend or terminate access when reasonably necessary because of violation of these Terms, fraud or suspected fraudulent activity, security threats, abuse of the Service, unlawful activity, or actions that threaten Squad Fee, its users, or third parties.",
        "Users may stop using Squad Fee at any time.",
      ],
    },
    {
      h: "Third-party services",
      body: [
        "Squad Fee may interact with or reference independent third-party services.",
        "Use of those services may be governed by separate terms and privacy policies.",
        "Rayven Tech LLC does not control independent third-party services and is not responsible for their operation.",
      ],
    },
    {
      h: "Disclaimer of warranties",
      body: [
        "To the maximum extent permitted by applicable law, Squad Fee is provided on an “as is” and “as available” basis.",
        "Rayven Tech LLC does not warrant that the Service will always be uninterrupted, error-free, completely secure, or suitable for every user’s particular needs.",
        "Nothing in these Terms excludes warranties or rights that cannot legally be excluded.",
      ],
    },
    {
      h: "Limitation of liability",
      body: [
        "To the maximum extent permitted by applicable law, Rayven Tech LLC will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages arising from or relating to use of Squad Fee.",
        "Rayven Tech LLC is not responsible for disputes, losses, errors, refunds, failed payments, or other issues arising from financial transactions conducted independently between users or through external payment services.",
        "Any limitations in these Terms apply only to the extent permitted by applicable law.",
      ],
    },
    {
      h: "Indemnification",
      body: [
        "To the extent permitted by applicable law, you agree to indemnify and hold harmless Rayven Tech LLC from claims, liabilities, damages, and reasonable expenses arising from your unlawful use of Squad Fee, violation of these Terms, or submission of content or information you did not have authority to provide.",
      ],
    },
    {
      h: "Governing law",
      body: [
        "These Terms are governed by the laws of the State of Florida, without regard to conflict-of-law principles, except where applicable law requires otherwise.",
        "Users may have rights under the laws of their own state or jurisdiction that cannot be waived by these Terms.",
      ],
    },
    {
      h: "Changes to these Terms",
      body: [
        "We may update these Terms as Squad Fee evolves.",
        "When appropriate, we may notify users of material changes through the Service, by email, or by updating the effective date above.",
        "Continued use after updated Terms become effective constitutes acceptance to the extent permitted by applicable law.",
      ],
    },
  ],
  contactIntro: "Questions regarding these Terms may be directed to:",
};
